'use client';

import { useEffect } from 'react';
import { trackReferralSignupStarted } from '@/analytics';
import { isReferralCode } from './referralCode';

/**
 * Referral Link Handler
 *
 * Captures referral codes from URL (?ref=ABC123) and stores them
 * for later application during signup
 */
export { isReferralCode } from './referralCode';

export function useReferralLink() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    if (isReferralCode(refCode)) {
      const normalised = refCode!.trim().toUpperCase();
      localStorage.setItem('referral_code', normalised);
      trackReferralSignupStarted(normalised, document.referrer || 'direct');
    }
  }, []);
}

/**
 * Get stored referral code
 */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('referral_code');
}

/**
 * Clear stored referral code (after successful application)
 */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('referral_code');
}

/**
 * Apply stored referral code for OAuth users
 * Called after OAuth login completes
 */
export async function applyStoredReferralCode(): Promise<{ success: boolean; message: string }> {
  const storedCode = getStoredReferralCode();
  
  if (!storedCode) {
    return { success: false, message: 'No stored referral code' };
  }

  try {
    const response = await fetch('/api/referral/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code: storedCode }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      clearStoredReferralCode();
      console.log('Referral code applied successfully:', storedCode);
      return { success: true, message: 'Referral code applied successfully' };
    } else {
      console.log('Failed to apply referral code:', data.error || data.message);
      return { success: false, message: data.error || data.message || 'Failed to apply' };
    }
  } catch (error) {
    console.error('Error applying referral code:', error);
    return { success: false, message: 'Error applying referral code' };
  }
}
